import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { db } from './Firebase/firebase';
import { ref, onValue } from 'firebase/database';
import CarAnimation from './Components/CarAnimation';
import Admin from './Pages/Admin';
import Login from './Pages/Login';
import ProtectedRoute from './Components/ProtectedRoute';
import './App.css';

function MainPage({ isDrawing, carState, participants, winner, countdownDate, rules }: {
  isDrawing: boolean;
  carState: { visible: boolean; showEffects: boolean };
  participants: string[];
  winner?: { id: string; name: string } | null;
  countdownDate: string | null;
  rules: string | null;
}) {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!countdownDate) return;

    const calculateTimeLeft = () => {
      const targetDate = new Date(countdownDate).getTime();
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [countdownDate]);

  const formatTimeLeft = () => {
    if (!timeLeft) return '';

    const isMobile = window.innerWidth <= 768;
    const dayLabel = isMobile ? 'd' : 'dní';
    const hourLabel = isMobile ? 'h' : 'hodin';
    const minuteLabel = isMobile ? 'm' : 'minut';
    const secondLabel = isMobile ? 's' : 'vteřin';

    return `Do slosování zbývá ${timeLeft.days > 0 ? `${timeLeft.days} ${dayLabel} ` : ''}${timeLeft.hours > 0 ? `${timeLeft.hours} ${hourLabel} ` : ''}${timeLeft.minutes > 0 ? `${timeLeft.minutes} ${minuteLabel} ` : ''}${timeLeft.seconds > 0 ? `${timeLeft.seconds} ${secondLabel}` : ''}`;
  };

  if (isDrawing) {
    return (
      <CarAnimation
        visible={carState.visible}
        showEffects={carState.showEffects}
        participants={participants}
      />
    );
  }

  if (winner) {
    return (
      <div className="winner-display">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="confetti" />
        ))}
        <h1>Losování</h1>
        <h2>Gratulejeme vyhráváte den na Redbull Ringu!</h2>
        <p className="winner-name">{winner.name}</p>
      </div>
    );
  } else {
    return (
      <div className="main-content">
        <h1 className="main-title">Vyhrajte den na Redbull Ringu!</h1>
        {countdownDate && (
          <div className="countdown">
            <p>{formatTimeLeft()}</p>
          </div>
        )}
        { rules && (
          <div className="rules">
            <h2>Chcete vyhrát den na Redbull Ringu?</h2>
            <div className="rules-content" dangerouslySetInnerHTML={{ __html: rules ?? '' }} />
            {/* <div className="rules-content"> */}
            {/* <ol>
              <li>Kup si lístek.</li>
              <li>Zaregistruj se na našem webu.</li>
              <li>Doraž 19.4. do Mostu.</li>
              <li>Měj u sebe lístek.</li>
              <li>Buď ve 14:20 u stage na vyhlášení.</li>
            </ol>
            <p>Držíme Ti palce a na viděnou v Mostě!</p> */}
            {/* </div> */}
          </div>
        )}
      </div>
    );
  }
}

function App() {
  const [isDrawing, setIsDrawing] = useState(false);
  const [participants, setParticipants] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [carState, setCarState] = useState({
    visible: false,
    showEffects: false
  });
  const [winner, setWinner] = useState<{ id: string; name: string } | null>(null);
  const [countdownDate, setCountdownDate] = useState<string | null>(null);
  const [rules, setRules] = useState<string | null>(null);

  useEffect(() => {
    let loadedCount = 0;
    const totalSubscriptions = 3;

    const handleDataLoaded = () => {
      loadedCount++;
      if (loadedCount === totalSubscriptions) {
        setLoading(false);
      }
    };

    const handleError = (error: Error) => {
      console.error('Firebase error:', error);
      setError('Failed to connect to the server. Please try again later.');
      setLoading(false);
    };

    try {
      // Subscribe to lottery status changes
      const lotteryRef = ref(db, 'lottery');
      const unsubscribeLottery = onValue(lotteryRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            setIsDrawing(data.isDrawing || false);
            setWinner(data.winner || null);
            setCountdownDate(data.countdownDate || null);
            setRules(data.rules || null);
          }
          handleDataLoaded();
        },
        handleError
      );

      // Subscribe to participants list
      const participantsRef = ref(db, 'participants');
      const unsubscribeParticipants = onValue(participantsRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data && data.list) {
            const namesArray = Object.values(data.list).map(value => String(value));
            setParticipants(namesArray);
          }
          handleDataLoaded();
        },
        handleError
      );

      // Subscribe to car state
      const carStateRef = ref(db, 'carState');
      const unsubscribeCarState = onValue(carStateRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            setCarState({
              visible: data.visible || false,
              showEffects: data.showEffects || false
            });
          }
          handleDataLoaded();
        },
        handleError
      );

      return () => {
        unsubscribeLottery();
        unsubscribeParticipants();
        unsubscribeCarState();
      };
    } catch (err) {
      handleError(err as Error);
    }
  }, []);

  if (error) {
    return (
      <div className="loading">
        <h1>Chyba</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading">
        <h1>Načítání...</h1>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <MainPage
            isDrawing={isDrawing}
            carState={carState}
            participants={participants}
            winner={winner}
            countdownDate={countdownDate}
            rules={rules}
          />
        } />
        <Route path="/login" element={<Login />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
